import React, { useEffect, useMemo, useState } from 'react';
import {
  InsightHero,
  RevenueCard,
  InsightCard,
  HowItWorksFooter,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { customersApi, Customer } from '../../../services/customersApi';
import { Insight, InsightHero as InsightHeroType, InsightTag } from './aiInsightsTypes';
import styles from './SalesRepAiInsights.module.css';

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function fmtMoney(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

/**
 * Derives a short list of "AI" insights from real customer metrics:
 *  • Reorder probability — customer with recent activity but no order in 7+ days
 *  • Churn risk — high revenue customer with no order in 30+ days
 *  • Upsell — customer with high revenue but low avg order value
 *  • Best visit — customer with recent positive momentum
 */
function deriveInsights(customers: Customer[]): Insight[] {
  const insights: Insight[] = [];

  for (const c of customers) {
    if (insights.length >= 6) break;
    const lastOrder = daysSince(c.metrics.last_order_at);
    const rev = c.metrics.total_revenue;
    const orders = c.metrics.order_count;
    const aov = c.metrics.avg_order_value;
    const name = c.store_name || c.full_name || c.email;

    if (rev > 0 && lastOrder !== null && lastOrder >= 7 && lastOrder < 30) {
      insights.push({
        id: `reorder-${c.id}`,
        icon: 'reorder',
        iconColor: 'blue',
        title: 'High Reorder Probability',
        customer: name,
        customerId: c.id,
        body: `${name} typically reorders frequently. Last order was ${lastOrder} days ago.`,
        recommendation: 'Create Order',
        tag: 'HIGH',
        confidence: `${Math.min(95, 70 + (lastOrder * 2))}% conf.`,
      });
      continue;
    }

    if (rev > 5000 && lastOrder !== null && lastOrder >= 30) {
      insights.push({
        id: `churn-${c.id}`,
        icon: 'churn',
        iconColor: 'red',
        title: 'Churn Risk Detected',
        customer: name,
        customerId: c.id,
        body: `Order frequency dropped — last order ${lastOrder} days ago, lifetime revenue ${fmtMoney(rev)}.`,
        recommendation: 'Schedule visit to understand needs',
        recommendationValue: 'Value: High Risk',
        tag: 'HIGH',
        confidence: `${Math.min(92, 60 + lastOrder)}% conf.`,
      });
      continue;
    }

    if (orders >= 5 && aov > 0 && aov < rev / orders / 1.5) {
      insights.push({
        id: `upsell-${c.id}`,
        icon: 'upsell',
        iconColor: 'green',
        title: 'Upsell Opportunity',
        customer: name,
        customerId: c.id,
        body: `Frequent buyer with low average order value. Suggest larger pack sizes or bundles.`,
        recommendation: 'Suggest premium product line',
        recommendationValue: `Value: +${fmtMoney(aov * 0.3)}/order`,
        tag: 'MEDIUM',
        confidence: '78% conf.',
      });
      continue;
    }

    if (rev > 1000 && lastOrder !== null && lastOrder < 7) {
      insights.push({
        id: `visit-${c.id}`,
        icon: 'visit',
        iconColor: 'pink',
        title: 'Strong Momentum',
        customer: name,
        customerId: c.id,
        body: `${name} is on a streak — last order ${lastOrder} days ago, ${orders} orders total.`,
        recommendation: 'Schedule a check-in visit this week',
        tag: 'LOW',
        confidence: '85% conf.',
      });
      continue;
    }
  }

  return insights;
}

const SalesRepAiInsights: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setError(null);
    customersApi.list({ limit: 200 })
      .then((r: any) => {
        const data = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        setCustomers(data);
        setLoading(false);
      })
      .catch((err) => { setError(err instanceof Error ? err.message : 'Failed'); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const insights = useMemo(() => deriveInsights(customers), [customers]);
  const hero: InsightHeroType = useMemo(() => {
    const high = insights.filter((i) => (i.tag as InsightTag) === 'HIGH').length;
    const conf = insights.length === 0
      ? '0%'
      : `${Math.round(insights.reduce((sum, i) => sum + parseInt(i.confidence, 10), 0) / insights.length)}%`;
    return {
      activeInsights: String(insights.length),
      highPriority: String(high),
      avgConfidence: conf,
    };
  }, [insights]);

  return (
    <div className={styles.container}>
      <InsightHero data={hero} />
      <RevenueCard />
      <AsyncBoundary
        loading={loading && customers.length === 0}
        error={error}
        empty={!loading && insights.length === 0}
        emptyMessage="No insights available yet — log activities and orders to generate signals"
        onRetry={load}
      >
        <div className={styles.list}>
          {insights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </div>
      </AsyncBoundary>
      <HowItWorksFooter />
    </div>
  );
};

export const SalesRepAiInsightsPage: React.FC = () => {
  return <SalesRepAiInsights />;
};

export { SalesRepAiInsights };
export default SalesRepAiInsights;
